import { Col, Row } from "antd";
import Tabs, { TabsProps } from "antd/es/tabs";
import React from "react";
import TimeAgo from "timeago-react";
import {
  InterviewDateBadge,
  InterviewDomainBadge,
  InterviewLocationBadge,
  InterviewQuestionsBadge,
  InterviewRoundsBadge,
  InterviewUserBadge
} from "../badge/InterviewExperience.Badges";
import { OfferReceivedBadge } from "../badge/OfferReceived.Badge";
import { PendingApprovalBadge } from "../badge/PendingApproval.Badge";
import { CompanyHelper } from "../company/Company.Helper";
import { CompanyImage } from "../company/Company.Image";
import { CopyClipboardButton } from "../components/common/CopyCliboard.button";
import { EditTitle } from "../components/common/Edit.title";
import { ReactionCountsViewComponent } from "../components/common/ReactionCountsViewComponent";
import { TextViewComponent } from "../components/common/TextView";
import { UrlHelpers } from "../helpers/Helpers";
import { Link } from "../links/Link";
import { AttachmentTargetType } from "../model/AttachmentTargetType";
import { InterviewExperienceResponse } from "../model/InterviewExperience";
import { InterviewExperienceRound } from "../model/InterviewExperienceRound";
import { QuestionAccordionView } from "../question/Question.view";
import { UserLink } from "../user/User.link";

interface InterviewExperienceDetailsProps {
  interviewExperience: InterviewExperienceResponse;
}

interface InterviewExperienceRoundViewProps {
  round: InterviewExperienceRound;
}

function InterviewExperienceRoundView(
  props: InterviewExperienceRoundViewProps
) {
  let round = props.round;
  return (
    <div>
      <Row align={"middle"}>
        <Col flex="auto">
          <h6 className="mb-0">{round.name}</h6>
        </Col>
        <Col>{round.durationDl}</Col>
      </Row>
      {round.roundDescription && (
        <p>
          <TextViewComponent text={round.roundDescription} />
        </p>
      )}
      {round.questions && round.questions.length > 0 && (
        <>
          <div className="mb-2">Problems Asked:</div>
          {round.questions.map((qc, index) => {
            return (
              <p key={index}>
                <QuestionAccordionView question={qc.question} expanded={true} />
              </p>
            );
          })}
        </>
      )}
    </div>
  );
}

function ExperienceMetadataView({
  ie,
}: {
  ie: InterviewExperienceResponse;
}) {
  const isNonEmpty = (val: any) => val != null && val !== "" && val !== "[]" && val !== "{}";
  const hasBackgroundData = (ie.yearsOfExperience != null && ie.yearsOfExperience > 0) ||
    isNonEmpty(ie.previousCompanies) ||
    isNonEmpty(ie.previousRoles) ||
    isNonEmpty(ie.backgroundSummary);

  return (
    <div className="mt-4">
      <style>{`
        .ie-section-card {
          padding: 20px 0;
          border-bottom: 1px solid #f0f0f0;
        }
        .ie-section-title {
          font-weight: 600;
          color: #333;
          margin-bottom: 16px;
          display: flex;
          align-items: center;
          gap: 10px;
        }
        .stat-label {
          color: #8c8c8c;
          font-size: 0.75rem;
          margin-bottom: 4px;
          text-transform: uppercase;
          letter-spacing: 0.5px;
        }
        .stat-value {
          font-weight: 400;
          color: #262626;
          font-size: 0.95rem;
        }
      `}</style>

      <Row gutter={[40, 0]}>
        {((ie.roleDomain && ie.roleDomain.toLowerCase() !== "other") || ie.level || ie.locationCity || ie.locationCountry) && (
          <Col xs={24} md={12}>
            <div className="ie-section-card">
              <div className="ie-section-title">
                <i className="fas fa-briefcase text-primary opacity-75"></i> Role Details
              </div>
              <Row gutter={[16, 20]}>
                {ie.roleDomain && ie.roleDomain.toLowerCase() !== "other" && <Col span={12}><div className="stat-label">Domain</div><div className="stat-value">{ie.roleDomain}</div></Col>}
                {ie.level && <Col span={12}><div className="stat-label">Level</div><div className="stat-value">{ie.level}</div></Col>}
                {(ie.locationCity || ie.locationCountry) && (
                  <Col span={24}>
                    <div className="stat-label">Location</div>
                    <div className="stat-value">{[ie.locationCity, ie.locationCountry].filter(Boolean).join(", ")}</div>
                  </Col>
                )}
              </Row>
            </div>
          </Col>
        )}

        {hasBackgroundData && (
          <Col xs={24} md={12}>
            <div className="ie-section-card border-0">
              <div className="ie-section-title">
                <i className="fas fa-user-graduate text-success opacity-75"></i> Candidate Background
              </div>
              <Row gutter={[16, 20]}>
                {ie.yearsOfExperience != null && ie.yearsOfExperience > 0 && (
                  <Col span={12}>
                    <div className="stat-label">Experience</div>
                    <div className="stat-value">{ie.yearsOfExperience} Years</div>
                  </Col>
                )}
                {isNonEmpty(ie.previousCompanies) && (
                  <Col span={24}>
                    <div className="stat-label">Previous Companies</div>
                    <div className="stat-value">{ie.previousCompanies}</div>
                  </Col>
                )}
              </Row>
              {isNonEmpty(ie.backgroundSummary) && <div className="mt-3 p-3 bg-light rounded-2 small text-muted border-start border-3 border-secondary-subtle">{ie.backgroundSummary}</div>}
            </div>
          </Col>
        )}

        {(ie.hiringChannelType && ie.hiringChannelType !== 'Other') && (
          <Col xs={24} md={12}>
            <div className="ie-section-card border-0">
              <div className="ie-section-title">
                <i className="fas fa-route text-info opacity-75"></i> Hiring Channel
              </div>
              <div>
                <div className="stat-label">Source</div>
                <div className="stat-value">{ie.hiringChannelType}</div>
                {isNonEmpty(ie.hiringChannelCollege) && <div className="mt-2 small text-muted"><i className="fas fa-university mr-1"></i> {ie.hiringChannelCollege}</div>}
              </div>
            </div>
          </Col>
        )}

        {(ie.orgName || ie.teamName) && (
          <Col xs={24} md={12}>
            <div className="ie-section-card border-0">
              <div className="ie-section-title">
                <i className="fas fa-sitemap text-warning opacity-75"></i> Team Info
              </div>
              <Row gutter={[16, 16]}>
                {ie.orgName && <Col span={12}><div className="stat-label">Organization</div><div className="stat-value">{ie.orgName}</div></Col>}
                {ie.teamName && <Col span={12}><div className="stat-label">Team</div><div className="stat-value">{ie.teamName}</div></Col>}
              </Row>
            </div>
          </Col>
        )}

        {(ie.preparationStrategy || ie.feedbackFromCompany || ie.generalNotes) && (
          <Col xs={24}>
            <div className="mt-4 pt-4 border-top">
              <h5 className="mb-4">Insights & Strategy</h5>
              {ie.preparationStrategy && (
                <div className="mb-4">
                  <div className="stat-label mb-2">Preparation Strategy</div>
                  <div className="text-change-line-for-newline text-secondary">{ie.preparationStrategy}</div>
                </div>
              )}
              {ie.feedbackFromCompany && (
                <div className="mb-4">
                  <div className="stat-label mb-2">Feedback from Company</div>
                  <div className="p-3 bg-light rounded-2 text-change-line-for-newline border-start border-primary border-3">{ie.feedbackFromCompany}</div>
                </div>
              )}
              {ie.generalNotes && (
                <div>
                  <div className="stat-label mb-2">General Notes</div>
                  <div className="text-change-line-for-newline text-secondary">{ie.generalNotes}</div>
                </div>
              )}
            </div>
          </Col>
        )}
      </Row>
    </div>
  );
}


export function InterviewExperienceDetails(
  props: InterviewExperienceDetailsProps
) {
  let ie = props.interviewExperience;

  const items: TabsProps["items"] = [
    {
      key: "summary",
      label: "Summary",
      children: (
        <div>
          {ie.summary && <TextViewComponent text={ie.summary} />}
          <ExperienceMetadataView ie={ie} />
        </div>
      ),
    },
  ];

  if (ie.rounds) {
    for (let round of ie.rounds) {
      items.push({
        key: round.number + "",
        label: "Round " + round.number,
        children: <InterviewExperienceRoundView round={round} />,
      });
    }
  }

  const onTabChange = (key: string) => {
    console.log(key);
  };

  return (
    <div>
      <style>{`
        .hero-title {
          font-size: 1.5rem;
        }
        .hero-meta {
          display: flex;
          flex-wrap: wrap;
          gap: 12px;
          align-items: center;
          margin-top: 12px;
        }
      `}</style>

      {ie ? (
        <div>
          <div className="hero-section py-2">
            <Row align="top" gutter={[24, 16]} wrap={false}>
              {ie.company && (
                <Col flex="none">
                  <div className="hero-company-box">
                    <Link
                      href={CompanyHelper.getViewUrl(ie.company)}
                      className="text-decoration-none"
                      target="_blank"
                    >
                      <CompanyImage company={ie.company} />
                    </Link>
                  </div>
                </Col>
              )}
              <Col flex="auto">
                <h1 className="hero-title">{ie.dl}</h1>
                <div className="hero-meta">
                  <InterviewDateBadge date={ie.createdAt}>
                    {ie.createdAt && <TimeAgo datetime={ie.createdAt} />}
                  </InterviewDateBadge>
                  {!ie.isAnon && (
                    <InterviewUserBadge>
                      <UserLink user={ie.postedBy} />
                    </InterviewUserBadge>
                  )}
                  <InterviewLocationBadge city={ie.locationCity} country={ie.locationCountry} />
                  <InterviewDomainBadge domain={ie.roleDomain} showSuffix={true} />
                  <InterviewRoundsBadge count={ie.roundsCount} />
                  <InterviewQuestionsBadge count={ie.questionsCount} />
                  {ie.receivedOffer && (
                    <div className="ms-1">
                      <OfferReceivedBadge />
                    </div>
                  )}
                  {ie.shareContent && (
                    <CopyClipboardButton
                      className="btn-link"
                      textToCopy={ie.shareContent}
                      dl="Copy summary"
                      successDl="Copied!"
                    />
                  )}

                </div>
              </Col>
              {ie.canUserEdit && (
                <Col>
                  <div className="d-flex flex-column align-items-end gap-2 text-end">
                    <Link
                      href={UrlHelpers.getEditInterviewExperienceUrl(ie.slug, ie.id)}
                      className="btn btn-primary py-1"
                    >
                      <EditTitle text="Edit" />

                    </Link>

                    {!ie.approvedAt && <PendingApprovalBadge />}
                  </div>
                </Col>
              )}
            </Row>
          </div>

          <Tabs
            defaultActiveKey="summary"
            items={items}
            onChange={onTabChange}
          />
          <div className="mt-3 d-flex gap-4 align-items-center">
            {ie.reactionCounts && (
              <ReactionCountsViewComponent
                targetType={AttachmentTargetType.INTERVIEW_EXPERIENCE}
                targetId={"" + ie.id}
                reactionCounts={ie.reactionCounts}
              />
            )}
          </div>
        </div>
      ) : null}
    </div>
  );
}
